import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Plus, Search, X, Trash2, Paperclip, FileText, Handshake, FileSignature, Smile } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  messagesService,
  MessageAttachmentMeta,
  deserializeMessagePayload,
} from '../services/messagesService';
import { supabase } from '../lib/supabase';
import { ContentModerationError, assertContentIsAllowed } from '../lib/contentModeration';

const QUICK_EMOJIS = [
  '😀', '😁', '😂', '🤣', '😍', '🤩', '🥳', '😎',
  '🙌', '👏', '🤝', '💡', '🔥', '❤️', '💼', '📝'
] as const;

const MessagesView: React.FC = () => {
  const { user } = useAuth();
  const { select, locale, t } = useLanguage();
  const [conversations, setConversations] = useState<Record<string, unknown>[]>([]);
  const [activeConversation, setActiveConversation] = useState<Record<string, unknown> | null>(null);
  const [messages, setMessages] = useState<Record<string, unknown>[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Record<string, unknown>[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  type PendingAttachment = { file: File; previewUrl: string };

  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [sendError, setSendError] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messageInputRef = useRef<HTMLInputElement | null>(null);
  const emojiPickerRef = useRef<HTMLDivElement | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeOptionsMessageId, setActiveOptionsMessageId] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      attachments.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    };
  }, [attachments]);

  const handleAttachmentClick = () => {
    setShowEmojiPicker(false);
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const mapped: PendingAttachment[] = Array.from(files).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setAttachments((prev) => [...prev, ...mapped]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    event.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => {
      const target = prev[index];
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleEmojiToggle = () => {
    setShowEmojiPicker(prev => !prev);
  };

  const handleEmojiSelect = (emoji: string) => {
    setNewMessage(prev => `${prev}${emoji}`);
    setShowEmojiPicker(false);
    messageInputRef.current?.focus();
  };

  const handleInsertTemplate = (type: 'budget' | 'collaboration' | 'contract') => {
    const templates = {
      budget: select({
        es: 'Hola, te comparto el presupuesto estimado para este proyecto. Avísame si deseas que ajustemos algún punto.',
        en: 'Hi! Sharing the estimated budget for this project. Let me know if you would like to adjust anything.',
      }),
      collaboration: select({
        es: 'Me gustaría proponerte una colaboración. Podemos coordinar detalles y fechas si te interesa.',
        en: 'I would love to propose a collaboration. We can align on details and timing if you are interested.',
      }),
      contract: select({
        es: 'Adjunto el borrador del contrato para tu revisión. Por favor revisa los términos y dime si todo está correcto.',
        en: 'Sharing the draft contract for your review. Please look over the terms and tell me if everything looks good.',
      }),
    } as const;

    const templateText = templates[type];
    setNewMessage(prev => (prev ? `${prev}\n\n${templateText}` : templateText));
    setShowEmojiPicker(false);
    messageInputRef.current?.focus();
  };

  useEffect(() => {
    void loadConversations();
    
    // Check if we should open a specific conversation
    const conversationId = sessionStorage.getItem('openConversationId');
    if (conversationId) {
      sessionStorage.removeItem('openConversationId');
      // Find and open the conversation
      const conversation = conversations.find(c => (c as Record<string, unknown>).id === conversationId);
      if (conversation) {
        handleConversationClick(conversation);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeConversation) {
      void loadMessages(activeConversation.id as string);
      void markMessagesAsRead(activeConversation.id as string);
      setAttachments([]);
      setNewMessage('');
      setShowEmojiPicker(false);
      
      // Subscribe to new messages
      const subscription = messagesService.subscribeToMessages(
        activeConversation.id as string,
        (message) => {
          setMessages(prev => [...prev, message as Record<string, unknown>]);
          void markMessagesAsRead(activeConversation.id as string);
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation]);

  useEffect(() => {
    if (userSearchQuery.trim().length > 0) {
      void searchUsers();
    } else {
      setSearchResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!emojiPickerRef.current) return;
      if (
        showEmojiPicker &&
        event.target instanceof Node &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await messagesService.getConversations();
      setConversations(data);
    } catch (err) {
      console.error('Error loading conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId: string) => {
    try {
      const data = await messagesService.getMessages(conversationId);
      setMessages(data);
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  };

  const markMessagesAsRead = async (conversationId: string) => {
    try {
      await messagesService.markMessagesAsRead(conversationId);
      // Update local state to show messages as read
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, unreadCount: 0 }
          : conv
      ));
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  const searchUsers = async () => {
    if (!userSearchQuery.trim()) return;
    
    setSearchingUsers(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, full_name, avatar_url, is_premium')
        .or(`username.ilike.%${userSearchQuery}%,full_name.ilike.%${userSearchQuery}%`)
        .neq('id', user?.id)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const startNewConversation = async (otherUser: Record<string, unknown>) => {
    try {
      const conversationId = await messagesService.getOrCreateConversation(otherUser.id as string);
      
      // Find the conversation in our list or create a new one
      let conversation = conversations.find(c => (c as Record<string, unknown>).id === conversationId) as Record<string, unknown> | undefined;
      
      if (!conversation) {
        conversation = {
          id: conversationId,
          otherParticipant: otherUser,
          lastMessage: null,
          unreadCount: 0
        };
        setConversations(prev => [conversation, ...prev]);
      }
      
      setActiveConversation(conversation);
      setShowNewChatModal(false);
      setUserSearchQuery('');
      setSearchResults([]);
      setSendError('');
    } catch (err) {
      console.error('Error starting new conversation:', err);
      alert(select({ es: 'Error al iniciar conversación', en: 'Error starting conversation' }));
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeConversation) return;

    const trimmedMessage = newMessage.trim();
    if (!trimmedMessage && attachments.length === 0) return;

    setSendError('');

    try {
      // Ensure text passes moderation before uploading files
      await assertContentIsAllowed([trimmedMessage]);

      const uploadedAttachments: MessageAttachmentMeta[] = [];

      for (const { file } of attachments) {
        const meta = await messagesService.uploadAttachment(file);
        uploadedAttachments.push(meta);
      }

      const messagePayload = {
        text: trimmedMessage,
        attachments: uploadedAttachments,
      };

      const message = await messagesService.sendMessage(activeConversation.id, messagePayload);
      setMessages((prev) => [...prev, message]);
      setNewMessage('');
      setAttachments([]);
      setShowEmojiPicker(false);
      setSendError('');

      setConversations((prev) => prev.map((conv) =>
        conv.id === activeConversation.id
          ? { ...conv, lastMessage: message, last_message_at: new Date().toISOString() }
          : conv
      ));
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
              : 'moderation.block.generic';
        setSendError(t(key, 'Your content violates our community guidelines.'));
      } else {
        console.error('Error sending message:', err);
        setSendError(select({ es: 'Error al enviar el mensaje.', en: 'Error sending message.' }));
      }
    }
  };

  const handleMarkAsSpam = async (messageId: string) => {
    if (!window.confirm(select({ es: '¿Estás seguro de que quieres marcar este mensaje como spam?', en: 'Are you sure you want to mark this message as spam?' }))) {
      return;
    }
    try {
      await messagesService.markMessageAsSpam(messageId);
      // Visually remove the message from the conversation
      setMessages(prev => prev.filter(m => m.id !== messageId));
      setActiveOptionsMessageId(null);
      // Optionally, show a success feedback message
    } catch (err) {
      console.error('Error marking as spam:', err);
      alert(select({ es: 'No se pudo marcar el mensaje como spam.', en: 'Failed to mark message as spam.' }));
    }
  };

  const handleConversationClick = (conversation: Record<string, unknown>) => {
    const unreadCount = typeof conversation.unreadCount === 'number' ? conversation.unreadCount : 0;
    const updatedConversation = unreadCount > 0
      ? { ...conversation, unreadCount: 0 }
      : conversation;

    setActiveConversation(updatedConversation);
    setSendError('');
    if (unreadCount > 0) {
      setConversations(prev => prev.map(conv =>
        (conv as Record<string, unknown>).id === conversation.id ? updatedConversation : conv
      ));
      void markMessagesAsRead(conversation.id as string);
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (!window.confirm(select({ es: '¿Estás seguro de que quieres eliminar esta conversación?', en: 'Are you sure you want to delete this conversation?' }))) {
      return;
    }

    try {
      await messagesService.deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      alert(
        select({
          es: 'Error al eliminar conversación: ',
          en: 'Error deleting conversation: '
        }) + (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return select({ es: 'Ahora', en: 'Now' });
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return date.toLocaleDateString(locale);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.otherParticipant?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.otherParticipant?.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lastMessage?.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-200 h-96 rounded-xl"></div>
            <div className="md:col-span-2 bg-gray-200 h-96 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <MessageCircle className="text-purple-600" size={24} />
          {select({ es: 'Mensajes', en: 'Messages' })}
        </h1>
        <button
          onClick={() => setShowNewChatModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus size={16} />
          {select({ es: 'Nueva conversación', en: 'New conversation' })}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Conversations List */}
        <div className="bg-white rounded-xl border border-gray-200 h-96 md:h-[600px] flex flex-col">
          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={select({ es: 'Buscar conversaciones...', en: 'Search conversations...' })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Conversations */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length > 0 ? (
              filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => handleConversationClick(conversation)}
                  className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    activeConversation?.id === conversation.id ? 'bg-purple-50 border-purple-200' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src={conversation.otherParticipant?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                        alt={conversation.otherParticipant?.full_name || select({ es: 'Usuario', en: 'User' })}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      {conversation.unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {conversation.unreadCount}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {conversation.otherParticipant?.full_name || select({ es: 'Usuario desconocido', en: 'Unknown user' })}
                        </h3>
                        {conversation.lastMessage && (
                          <span className="text-xs text-gray-500">
                            {formatTime(conversation.lastMessage.created_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        @{conversation.otherParticipant?.username || select({ es: 'usuario', en: 'user' })}
                      </p>
                      {conversation.lastMessage && (() => {
                        const payload = deserializeMessagePayload(conversation.lastMessage.content);
                        const hasText = payload.text.trim().length > 0;
                        const hasImages = payload.attachments.some((att) => att.type === 'image');
                        const hasFiles = payload.attachments.some((att) => att.type === 'file');

                        let preview = payload.text.trim();

                        if (!hasText && payload.attachments.length > 0) {
                          if (hasImages && !hasFiles) {
                            preview = select({ es: '📷 Imagen adjunta', en: '📷 Image attachment' });
                          } else if (hasFiles && !hasImages) {
                            preview = select({ es: '📎 Archivo adjunto', en: '📎 File attachment' });
                          } else {
                            preview = select({ es: '📎 Adjuntos', en: '📎 Attachments' });
                          }
                        }

                        return (
                          <p className="text-sm text-gray-600 truncate mt-1">
                            {preview}
                          </p>
                        );
                      })()}
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteConversation(conversation.id);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title={select({ es: 'Eliminar conversación', en: 'Delete conversation' })}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <MessageCircle size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="font-medium text-gray-900 mb-2">{select({ es: 'No hay conversaciones', en: 'No conversations yet' })}</h3>
                <p className="text-gray-500 text-sm">{select({ es: 'Inicia una nueva conversación', en: 'Start a new conversation' })}</p>
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 h-96 md:h-[600px] flex flex-col">
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={activeConversation.otherParticipant?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                    alt={activeConversation.otherParticipant?.full_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {activeConversation.otherParticipant?.full_name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      @{activeConversation.otherParticipant?.username}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <button
                    type="button"
                    onClick={handleAttachmentClick}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-purple-500 hover:text-purple-600"
                    title={select({ es: 'Adjuntar archivos', en: 'Attach files' })}
                  >
                    <Paperclip size={16} />
                    <span className="hidden sm:inline">{select({ es: 'Adjuntar', en: 'Attach' })}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInsertTemplate('budget')}
                    className="inline-flex items-center gap-2 rounded-full border border-purple-100 bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-100"
                  >
                    <FileText size={16} />
                    <span className="hidden sm:inline">{select({ es: 'Enviar presupuesto', en: 'Send budget' })}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInsertTemplate('collaboration')}
                    className="inline-flex items-center gap-2 rounded-full border border-purple-100 bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-100"
                  >
                    <Handshake size={16} />
                    <span className="hidden sm:inline">{select({ es: 'Proponer colaboración', en: 'Propose collaboration' })}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInsertTemplate('contract')}
                    className="inline-flex items-center gap-2 rounded-full border border-purple-100 bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-100"
                  >
                    <FileSignature size={16} />
                    <span className="hidden sm:inline">{select({ es: 'Enviar contrato', en: 'Send contract' })}</span>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message) => {
                  const payload = deserializeMessagePayload(message.content);
                  const isMyMessage = message.sender_id === user?.id;
                  const bubbleClasses = isMyMessage
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-900';
                  const timestampClasses = isMyMessage ? 'text-purple-200' : 'text-gray-500';
                  const hasText = payload.text.trim().length > 0;

                  return (
                    <div
                      key={message.id}
                      className={`flex items-end gap-2 ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      {!isMyMessage && (
                        <div className="relative">
                          <button 
                            onClick={() => setActiveOptionsMessageId(activeOptionsMessageId === message.id ? null : message.id)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
                          </button>
                          {activeOptionsMessageId === message.id && (
                            <div className="absolute bottom-full left-0 mb-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                              <button
                                onClick={() => handleMarkAsSpam(message.id)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {select({ es: 'Marcar como Spam', en: 'Mark as Spam' })}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-xl space-y-2 ${bubbleClasses}`}>
                        {hasText && (
                          <p className="break-words whitespace-pre-line">{payload.text}</p>
                        )}

                        {payload.attachments.length > 0 && (
                          <div className="space-y-2">
                            {payload.attachments.map((attachment, index) => (
                              attachment.type === 'image' ? (
                                <a
                                  key={`${attachment.url}-${index}`}
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block overflow-hidden rounded-lg border border-white/20 bg-black/10"
                                >
                                  <img
                                    src={attachment.url}
                                    alt={attachment.name}
                                    className="max-h-60 w-full object-cover"
                                  />
                                </a>
                              ) : (
                                <a
                                  key={`${attachment.url}-${index}`}
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                                    message.sender_id === user?.id
                                      ? 'border-white/40 bg-white/10 text-white'
                                      : 'border-gray-200 bg-gray-50 text-gray-700'
                                  }`}
                                >
                                  <FileText size={16} />
                                  <span className="truncate">{attachment.name}</span>
                                </a>
                              )
                            ))}
                          </div>
                        )}

                        <p className={`text-xs ${timestampClasses}`}>{formatTime(message.created_at)}</p>
                      </div>
                      {isMyMessage && (
                        <div className="relative">
                          <button 
                            onClick={() => setActiveOptionsMessageId(activeOptionsMessageId === message.id ? null : message.id)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
                          </button>
                          {activeOptionsMessageId === message.id && (
                            <div className="absolute bottom-full right-0 mb-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                              {/* Add options for own messages here if needed, e.g., delete */}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <MessageCircle size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 mb-2">{select({ es: 'Inicia la conversación', en: 'Start the conversation' })}</h3>
                    <p className="text-gray-500 text-sm">{select({ es: 'Envía tu primer mensaje', en: 'Send your first message' })}</p>
                  </div>
                )}
              </div>

              {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
              {sendError && (
                <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {sendError}
                </div>
              )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleAttachmentChange}
                  className="hidden"
                />

                {attachments.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-3">
                    {attachments.map(({ file, previewUrl }, index) => (
                      <div key={`${file.name}-${index}`} className="relative">
                        {file.type.startsWith('image/') ? (
                          <img
                            src={previewUrl}
                            alt={file.name}
                            className="h-20 w-20 rounded-lg object-cover shadow"
                          />
                        ) : (
                          <div className="flex h-20 w-36 items-center justify-center rounded-lg border border-gray-300 bg-gray-50 px-3 text-xs font-medium text-gray-600">
                            <FileText size={16} className="mr-2" />
                            <span className="truncate">{file.name}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          className="absolute -top-2 -right-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-purple-600 shadow hover:bg-purple-50"
                          title={select({ es: 'Quitar adjunto', en: 'Remove attachment' })}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div ref={emojiPickerRef} className="relative flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleAttachmentClick}
                      className="hidden sm:inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors hover:border-purple-500 hover:text-purple-600"
                      title={select({ es: 'Adjuntar archivos', en: 'Attach files' })}
                    >
                      <Paperclip size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={handleEmojiToggle}
                      className={`inline-flex h-11 w-11 items-center justify-center rounded-lg border text-gray-500 transition-colors ${
                        showEmojiPicker ? 'border-purple-500 text-purple-600' : 'border-gray-300 hover:border-purple-500 hover:text-purple-600'
                      }`}
                      title={select({ es: 'Insertar emoji', en: 'Insert emoji' })}
                    >
                      <Smile size={18} />
                    </button>
                    {showEmojiPicker && (
                      <div className="absolute bottom-12 left-0 z-30 w-56 rounded-2xl border border-gray-200 bg-white p-3 shadow-xl">
                        <div className="grid grid-cols-8 gap-2 text-base">
                          {QUICK_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => handleEmojiSelect(emoji)}
                              className="rounded-lg p-1 text-lg hover:bg-purple-50"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    ref={messageInputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => {
                      setSendError('');
                      setNewMessage(e.target.value);
                    }}
                    placeholder={select({ es: 'Escribe un mensaje...', en: 'Type a message...' })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() && attachments.length === 0}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle size={64} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-medium text-gray-900 mb-2">{select({ es: 'Selecciona una conversación', en: 'Select a conversation' })}</h3>
                <p className="text-gray-500">{select({ es: 'Elige una conversación para empezar a chatear', en: 'Choose a conversation to start chatting' })}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">{select({ es: 'Nueva conversación', en: 'New conversation' })}</h2>
                <button
                  onClick={() => {
                    setShowNewChatModal(false);
                    setUserSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={select({ es: 'Buscar usuarios...', en: 'Search users...' })}
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {searchingUsers && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                  </div>
                )}

                <div className="max-h-60 overflow-y-auto space-y-2">
                  {searchResults.map((userResult) => (
                    <button
                      key={userResult.id}
                      onClick={() => startNewConversation(userResult)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
                    >
                      <img
                        src={userResult.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop'}
                        alt={userResult.full_name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{userResult.full_name}</h4>
                        <p className="text-sm text-gray-500">@{userResult.username}</p>
                      </div>
                      {userResult.is_premium && (
                        <span className="text-yellow-500 text-sm">✨</span>
                      )}
                    </button>
                  ))}
                  
                  {userSearchQuery && !searchingUsers && searchResults.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">{select({ es: 'No se encontraron usuarios', en: 'No users found' })}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesView;
